import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { 
  ArrowLeft, Users, Activity, Calendar, Clock, 
  Monitor, Smartphone, Tablet, Globe, Search,
  RefreshCw, ChevronLeft, ChevronRight, Filter
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ActivityLogs() {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [todayLogins, setTodayLogins] = useState(null);
  const [onlineUsers, setOnlineUsers] = useState(null);
  const [logs, setLogs] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [page, setPage] = useState(1);
  const [dateFilter, setDateFilter] = useState('today');
  const [searchTerm, setSearchTerm] = useState('');

  const fetchData = async () => {
    setLoading(true);
    try {
      const [statsRes, todayRes, onlineRes] = await Promise.all([
        axios.get(`${API}/admin/activity-stats`, { withCredentials: true }),
        axios.get(`${API}/admin/today-logins`, { withCredentials: true }),
        axios.get(`${API}/admin/online-users-detailed`, { withCredentials: true })
      ]);
      setStats(statsRes.data);
      setTodayLogins(todayRes.data);
      setOnlineUsers(onlineRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
    }
    setLoading(false);
  };

  const fetchLogs = async () => {
    try {
      const params = new URLSearchParams({
        page: page.toString(),
        limit: '30',
        date_filter: dateFilter
      });
      if (searchTerm) params.append('search', searchTerm);
      
      const res = await axios.get(`${API}/admin/activity-logs?${params}`, { withCredentials: true });
      setLogs(res.data);
    } catch (error) {
      console.error('Error fetching logs:', error);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    if (activeTab === 'logs') {
      fetchLogs();
    }
  }, [activeTab, page, dateFilter, searchTerm]);

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    return date.toLocaleString('tr-TR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getDeviceIcon = (device) => {
    if (device === 'Mobile') return <Smartphone className="w-4 h-4" />;
    if (device === 'Tablet') return <Tablet className="w-4 h-4" />;
    return <Monitor className="w-4 h-4" />;
  };

  if (loading && !stats) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-gradient-to-r from-indigo-600 to-purple-600 text-white p-4">
        <div className="max-w-6xl mx-auto">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <button onClick={() => navigate('/admin')} className="p-2 hover:bg-white/10 rounded-lg">
                <ArrowLeft className="w-5 h-5" />
              </button>
              <div>
                <h1 className="text-xl font-bold flex items-center gap-2">
                  <Activity className="w-6 h-6" />
                  Aktivite Logları
                </h1>
                <p className="text-indigo-100 text-sm">Kullanıcı giriş ve aktivite takibi</p>
              </div>
            </div>
            <Button 
              onClick={fetchData}
              className="bg-white/10 hover:bg-white/20"
            >
              <RefreshCw className="w-4 h-4 mr-2" />
              Yenile
            </Button>
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto p-4">
        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <Card className="bg-gradient-to-br from-green-400 to-emerald-500 border-0">
            <CardContent className="p-4 text-white">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center">
                  <Users className="w-6 h-6" />
                </div>
                <div>
                  <p className="text-3xl font-bold">{onlineUsers?.online_count || 0}</p>
                  <p className="text-sm opacity-90">Şu An Online</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-gradient-to-br from-blue-400 to-indigo-500 border-0">
            <CardContent className="p-4 text-white">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center">
                  <Calendar className="w-6 h-6" />
                </div>
                <div>
                  <p className="text-3xl font-bold">{stats?.today?.unique_users || 0}</p>
                  <p className="text-sm opacity-90">Bugün Giren</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-gradient-to-br from-amber-400 to-orange-500 border-0">
            <CardContent className="p-4 text-white">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center">
                  <Activity className="w-6 h-6" />
                </div>
                <div>
                  <p className="text-3xl font-bold">{stats?.today?.logins || 0}</p>
                  <p className="text-sm opacity-90">Bugün Giriş</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-gradient-to-br from-purple-400 to-violet-500 border-0">
            <CardContent className="p-4 text-white">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center">
                  <Globe className="w-6 h-6" />
                </div>
                <div>
                  <p className="text-3xl font-bold">{stats?.this_week?.unique_users || 0}</p>
                  <p className="text-sm opacity-90">Bu Hafta</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Tabs */}
        <div className="flex gap-2 mb-4">
          <Button 
            variant={activeTab === 'overview' ? 'default' : 'outline'}
            onClick={() => setActiveTab('overview')}
          >
            Genel Bakış
          </Button>
          <Button 
            variant={activeTab === 'online' ? 'default' : 'outline'}
            onClick={() => setActiveTab('online')}
          >
            Online Kullanıcılar
          </Button>
          <Button 
            variant={activeTab === 'today' ? 'default' : 'outline'}
            onClick={() => setActiveTab('today')}
          >
            Bugün Girenler
          </Button>
          <Button 
            variant={activeTab === 'logs' ? 'default' : 'outline'}
            onClick={() => setActiveTab('logs')}
          >
            Tüm Loglar
          </Button>
        </div>

        {/* Tab Content */}
        {activeTab === 'overview' && (
          <div className="grid md:grid-cols-2 gap-6">
            {/* Online Users Mini */}
            <Card>
              <CardContent className="p-4">
                <h3 className="font-bold text-gray-800 mb-3 flex items-center gap-2">
                  <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
                  Şu An Online ({onlineUsers?.online_count || 0})
                </h3>
                <div className="space-y-2 max-h-[300px] overflow-y-auto">
                  {onlineUsers?.users?.slice(0, 10).map((user, idx) => (
                    <div key={idx} className="flex items-center justify-between p-2 bg-gray-50 rounded-lg">
                      <div className="flex items-center gap-2">
                        {getDeviceIcon(user.device_type)}
                        <span className="text-sm font-medium">{user.email}</span>
                      </div>
                      <span className="text-xs text-gray-500">{user.browser}</span>
                    </div>
                  ))}
                  {(!onlineUsers?.users || onlineUsers.users.length === 0) && (
                    <p className="text-gray-500 text-center py-4">Şu an online kullanıcı yok</p>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Today's Logins Mini */}
            <Card>
              <CardContent className="p-4">
                <h3 className="font-bold text-gray-800 mb-3 flex items-center gap-2">
                  <Calendar className="w-4 h-4 text-blue-500" />
                  Bugün Giriş Yapanlar ({todayLogins?.unique_users || 0} kişi)
                </h3>
                <div className="space-y-2 max-h-[300px] overflow-y-auto">
                  {todayLogins?.logins?.slice(0, 10).map((login, idx) => (
                    <div key={idx} className="flex items-center justify-between p-2 bg-gray-50 rounded-lg">
                      <div>
                        <p className="text-sm font-medium">{login.email}</p>
                        <p className="text-xs text-gray-500">{login.ip_address}</p>
                      </div>
                      <div className="text-right">
                        <p className="text-xs text-gray-600">{formatDate(login.timestamp)}</p>
                        <div className="flex items-center gap-1 text-xs text-gray-500">
                          {getDeviceIcon(login.device_type)}
                          {login.browser}
                        </div>
                      </div>
                    </div>
                  ))}
                  {(!todayLogins?.logins || todayLogins.logins.length === 0) && (
                    <p className="text-gray-500 text-center py-4">Bugün henüz giriş yok</p>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {activeTab === 'online' && (
          <Card>
            <CardContent className="p-4">
              <h3 className="font-bold text-gray-800 mb-4 flex items-center gap-2">
                <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
                Online Kullanıcılar ({onlineUsers?.online_count || 0})
              </h3>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="text-left p-3 text-sm font-semibold text-gray-600">Email</th>
                      <th className="text-left p-3 text-sm font-semibold text-gray-600">IP Adresi</th>
                      <th className="text-left p-3 text-sm font-semibold text-gray-600">Cihaz</th>
                      <th className="text-left p-3 text-sm font-semibold text-gray-600">Tarayıcı</th>
                      <th className="text-left p-3 text-sm font-semibold text-gray-600">Son Görülme</th>
                    </tr>
                  </thead>
                  <tbody>
                    {onlineUsers?.users?.map((user, idx) => (
                      <tr key={idx} className="border-b hover:bg-gray-50">
                        <td className="p-3 text-sm">{user.email}</td>
                        <td className="p-3 text-sm font-mono text-gray-600">{user.ip_address}</td>
                        <td className="p-3 text-sm flex items-center gap-1">
                          {getDeviceIcon(user.device_type)}
                          {user.device_type}
                        </td>
                        <td className="p-3 text-sm">{user.browser}</td>
                        <td className="p-3 text-sm text-gray-500">{formatDate(user.last_seen)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {(!onlineUsers?.users || onlineUsers.users.length === 0) && (
                  <p className="text-gray-500 text-center py-8">Şu an online kullanıcı yok</p>
                )}
              </div>
            </CardContent>
          </Card>
        )}

        {activeTab === 'today' && (
          <Card>
            <CardContent className="p-4">
              <h3 className="font-bold text-gray-800 mb-4 flex items-center gap-2">
                <Calendar className="w-5 h-5 text-blue-500" />
                Bugün Giriş Yapanlar - {todayLogins?.date}
                <span className="ml-2 text-sm font-normal text-gray-500">
                  ({todayLogins?.unique_users || 0} kişi, {todayLogins?.total_logins || 0} giriş)
                </span>
              </h3>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="text-left p-3 text-sm font-semibold text-gray-600">Saat</th>
                      <th className="text-left p-3 text-sm font-semibold text-gray-600">Email</th>
                      <th className="text-left p-3 text-sm font-semibold text-gray-600">IP Adresi</th>
                      <th className="text-left p-3 text-sm font-semibold text-gray-600">Cihaz</th>
                      <th className="text-left p-3 text-sm font-semibold text-gray-600">Tarayıcı</th>
                    </tr>
                  </thead>
                  <tbody>
                    {todayLogins?.logins?.map((login, idx) => (
                      <tr key={idx} className="border-b hover:bg-gray-50">
                        <td className="p-3 text-sm">{formatDate(login.timestamp)}</td>
                        <td className="p-3 text-sm font-medium">{login.email}</td>
                        <td className="p-3 text-sm font-mono text-gray-600">{login.ip_address}</td>
                        <td className="p-3 text-sm flex items-center gap-1">
                          {getDeviceIcon(login.device_type)}
                          {login.device_type}
                        </td>
                        <td className="p-3 text-sm">{login.browser}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {(!todayLogins?.logins || todayLogins.logins.length === 0) && (
                  <p className="text-gray-500 text-center py-8">Bugün henüz giriş yok</p>
                )}
              </div>
            </CardContent>
          </Card>
        )}

        {activeTab === 'logs' && (
          <Card>
            <CardContent className="p-4">
              {/* Filters */}
              <div className="flex flex-wrap gap-3 mb-4">
                <div className="flex items-center gap-2 bg-gray-100 rounded-lg p-1">
                  {['today', 'yesterday', 'week', 'month'].map((filter) => (
                    <button
                      key={filter}
                      onClick={() => { setDateFilter(filter); setPage(1); }}
                      className={`px-3 py-1 rounded-md text-sm transition-colors ${
                        dateFilter === filter ? 'bg-white shadow text-blue-600' : 'text-gray-600 hover:bg-gray-200'
                      }`}
                    >
                      {filter === 'today' ? 'Bugün' : filter === 'yesterday' ? 'Dün' : filter === 'week' ? 'Bu Hafta' : 'Bu Ay'}
                    </button>
                  ))}
                </div>
                <div className="flex-1 max-w-xs">
                  <div className="relative">
                    <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                    <input
                      type="text"
                      placeholder="Email veya IP ara..."
                      value={searchTerm}
                      onChange={(e) => { setSearchTerm(e.target.value); setPage(1); }}
                      className="w-full pl-10 pr-4 py-2 border rounded-lg text-sm"
                    />
                  </div>
                </div>
              </div>

              {/* Logs Table */}
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="text-left p-3 text-sm font-semibold text-gray-600">Tarih/Saat</th>
                      <th className="text-left p-3 text-sm font-semibold text-gray-600">Email</th>
                      <th className="text-left p-3 text-sm font-semibold text-gray-600">Aksiyon</th>
                      <th className="text-left p-3 text-sm font-semibold text-gray-600">IP Adresi</th>
                      <th className="text-left p-3 text-sm font-semibold text-gray-600">Cihaz</th>
                    </tr>
                  </thead>
                  <tbody>
                    {logs?.logs?.map((log, idx) => (
                      <tr key={idx} className="border-b hover:bg-gray-50">
                        <td className="p-3 text-sm">{formatDate(log.timestamp)}</td>
                        <td className="p-3 text-sm font-medium">{log.email}</td>
                        <td className="p-3">
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                            log.action === 'login' ? 'bg-green-100 text-green-700' : 'bg-blue-100 text-blue-700'
                          }`}>
                            {log.action}
                          </span>
                        </td>
                        <td className="p-3 text-sm font-mono text-gray-600">{log.ip_address}</td>
                        <td className="p-3 text-sm flex items-center gap-1">
                          {getDeviceIcon(log.device_type)}
                          {log.device_type} / {log.browser}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {(!logs?.logs || logs.logs.length === 0) && (
                  <p className="text-gray-500 text-center py-8">Log bulunamadı</p>
                )}
              </div>

              {/* Pagination */}
              {logs && logs.pages > 1 && (
                <div className="flex items-center justify-between mt-4">
                  <p className="text-sm text-gray-500">
                    Toplam {logs.total} kayıt, Sayfa {logs.page}/{logs.pages}
                  </p>
                  <div className="flex gap-2">
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={() => setPage(p => Math.max(1, p - 1))}
                      disabled={page === 1}
                    >
                      <ChevronLeft className="w-4 h-4" />
                    </Button>
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={() => setPage(p => Math.min(logs.pages, p + 1))}
                      disabled={page === logs.pages}
                    >
                      <ChevronRight className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}
